'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import type { LegacyView } from '../lib/legacy-shell-markup';
import { getCatalogUpdates, type CatalogUpdate } from '../lib/catalog-updates';
import { applyLegacyShellDynamicContent, updateFeaturedCatalogCards } from '../lib/legacy-shell-markup';
import { SiteHeader, SiteHero, SiteNavigation, type SiteSearchSuggestion } from './site-chrome';
import { useSiteNavigation, type SiteNavItem } from './use-site-navigation';
import { heroUrl } from '../lib/heroes/catalog';
import type { HeroDirectoryEntry } from '../lib/heroes/directory-types';
import type { HeroData } from '../lib/heroes/types';
import { heroToDirectoryEntry } from '../lib/heroes/directory-entry';
import {
  parseCatalogNavigation,
  type CatalogNavigationItem,
} from '../lib/catalog-navigation';
import { channelByRoute, featuredHeroes, wikiChannels, wikiGame } from '../lib/wiki-data';
import { getWikiGame } from '../lib/wiki-games';
import {
  bannerSlotsFromCatalog,
  catalogHeroesForBanner,
  type BannerHeroSource,
  type BannerLive2dSlot,
} from '../lib/heroes/banner';
import HomeAdminEditor from './home-admin-editor';
import {
  cloneHomepageConfig,
  createDefaultHomepageConfig,
  defaultHomepageConfig,
  fetchHomepageConfig,
  guideAlbumCoverDisplayURL,
  homepageDisplayImage,
  readHomepageConfig,
  rewriteHomepageSitePaths,
  saveHomepageConfig,
  updateHomepageConfig,
  updateWikiGameProfile,
  type HomepageConfig,
  type WikiGameProfile,
} from '../lib/homepage-config';
import {
  readWikiToken,
  readWikiUser,
  WIKI_AUTH_EVENT,
  type WikiUser,
} from '../lib/wiki-auth';
import { fetchMyWikiPermission, EMPTY_PERMISSION, type WikiPermission } from '../lib/wiki-permissions';
import { catalogCdnHost } from '../lib/config/cdn';
import { getStaticAssetPrefix, staticUrl } from '../lib/config/static-assets';
import { handleImgToWebp } from '../lib/config/image-webp';
import { fetchWikiSearch, type WikiSearchItem } from '../lib/wiki-search';

const SUGGESTION_DEBOUNCE_MS = 280;
const SUGGESTION_LIMIT = 7;
const formatUpdatedAgo = (value?: string) => {
  if (!value) return '最近更新';
  const updatedAt = new Date(value);
  if (Number.isNaN(updatedAt.getTime())) return '最近更新';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  updatedAt.setHours(0, 0, 0, 0);
  const days = Math.max(
    0,
    Math.floor((today.getTime() - updatedAt.getTime()) / 86_400_000),
  );
  return days === 0 ? '今天更新' : `${days} 天前更新`;
};

const selectors: Record<LegacyView, string> = {
  home: '.content-shell',
  catalog: '.catalog-layout',
  guides: '.guides-layout',
  tools: '.tools-layout',
  benefits: '.benefits-layout',
  community: '.community-layout',
};
type GuideSummary = {
  id: string;
  title: string;
  excerpt: string;
  category?: string;
  author: string;
  publishedAt?: string;
};
const communityDirectories = ['社区', '资讯', '公告'] as const;
type CommunityDirectory = (typeof communityDirectories)[number];
const communityDirectoryForPost = (post: {
  type?: string;
  category?: string | null;
}): CommunityDirectory => {
  const category = String(post.category || '').trim();
  if (communityDirectories.includes(category as CommunityDirectory)) {
    return category as CommunityDirectory;
  }
  if (category.includes('公告')) return '公告';
  if (
    post.type === 'news' ||
    category.includes('资讯') ||
    category.includes('情报')
  ) {
    return '资讯';
  }
  return '社区';
};
const formatCommunityRelativeTime = (value?: string) => {
  if (!value) return '刚刚';
  const publishedAt = new Date(value);
  if (Number.isNaN(publishedAt.getTime())) return '刚刚';
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - publishedAt.getTime()) / 60_000),
  );
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  return publishedAt.toLocaleDateString('zh-CN');
};
function initialHomepageConfig(
  gameKey: string,
  gameDomain: string,
  gameName: string,
  gameIcon?: string,
) {
  const config = cloneHomepageConfig();
  if (gameKey === wikiGame.key)
    return gameDomain === wikiGame.key
      ? config
      : (JSON.parse(
          JSON.stringify(config).replaceAll(
            `/${wikiGame.key}`,
            `/${gameDomain}`,
          ),
        ) as HomepageConfig);
  config.heroBackground = gameIcon || config.heroBackground;
  config.toolsEnabled = false;
  config.benefitsEnabled = false;
  config.versionTitle = `${gameName} 攻略`;
  config.featuredGuide = {
    title: '攻略内容待添加',
    image: gameIcon || config.featuredGuide.image,
    url: `/${gameDomain}/dguides`,
  };
  config.guideLinks = [
    {
      title: '攻略内容待添加',
      description: '可在 Wiki 管理中添加攻略推荐',
      image: gameIcon || config.featuredGuide.image,
      url: `/${gameDomain}/dguides`,
    },
  ];
  config.guideRecommendations = [];
  config.notices = [
    {
      id: 'wiki-created',
      type: '公告',
      title: `${gameName} Wiki 已创建`,
      url: `/${gameDomain}`,
    },
  ];
  config.benefits = [];
  config.promotions = [];
  config.newcomerLinks = [];
  config.catalogLinks = [];
  config.toolLinks = [];
  config.toolCards = [];
  return config;
}
export default function LegacyHomeClient({
  view: fallbackView,
  gameKey,
  gameDomain,
  gameName,
  gameIcon,
  heroDirectory: initialHeroDirectory,
  bannerHeroes: initialBannerHeroes = [],
  bannerLive2dSlots: initialBannerLive2dSlots = [],
  catalogNavigation: initialCatalogNavigation,
  initialHomepageConfig: serverHomepageConfig,
  markup: initialMarkup,
  clientBootstrap = false,
}: {
  view: LegacyView;
  gameKey: string;
  gameDomain: string;
  gameName: string;
  gameIcon?: string;
  heroDirectory: HeroDirectoryEntry[];
  bannerHeroes?: BannerHeroSource[];
  bannerLive2dSlots?: BannerLive2dSlot[];
  catalogNavigation: CatalogNavigationItem[];
  initialHomepageConfig?: HomepageConfig | null;
  markup: string;
  /** 用户侧薄壳：挂载后拉 catalog-heroes / catalogs / homepage 再填充 */
  clientBootstrap?: boolean;
}) {
  const pathname = usePathname();
  const route = pathname.split('/').filter(Boolean)[1];
  const view: LegacyView = route ? channelByRoute[route] || fallbackView : 'home';
  const initialView = useRef(view);
  const [heroDirectory, setHeroDirectory] = useState(initialHeroDirectory);
  const [bannerHeroes, setBannerHeroes] = useState(initialBannerHeroes);
  const [bannerLive2dSlots, setBannerLive2dSlots] = useState(initialBannerLive2dSlots);
  const [catalogNavigation, setCatalogNavigation] = useState(initialCatalogNavigation);
  const [markup] = useState(initialMarkup);
  const [catalogUpdates, setCatalogUpdates] = useState<CatalogUpdate[] | undefined>();
  const [shellDataReady, setShellDataReady] = useState(!clientBootstrap);
  // Keep the HTML string stable between tabs: replacing it would discard the
  // legacy widgets' DOM and listeners. SSR selects the requested tab as well.
  const contentMarkup = useMemo(() => markup.replace(
    /<(section|main) class="(content-shell|catalog-layout|guides-layout|tools-layout|benefits-layout|community-layout)([^"]*)"( hidden)?/g,
    (match, tag: string, className: string, suffix: string) => {
      const selected = selectors[initialView.current] === `.${className}`;
      return `<${tag} class="${className}${suffix}"${selected ? '' : ' hidden'}`;
    },
  ), [markup]);
  const contentNode = useMemo(() => (
    <div className="legacy-game-home" dangerouslySetInnerHTML={{ __html: contentMarkup }} />
  ), [contentMarkup]);
  const base = `/${gameDomain}`;
  const router = useRouter();
  const routeSearchParams = useSearchParams();
  const openGuide = useCallback(
    (href: string) => {
      document.documentElement.classList.add('is-guide-route-transition');
      // SPA / App Router 均走客户端路由，避免深链整页 document 重载。
      router.push(href);
    },
    [router],
  );
  const navigation = useMemo<SiteNavItem<LegacyView>[]>(
    () => [
      { id: 'home', title: '首页', href: `/${gameDomain}` },
      { id: 'catalog', title: '图鉴', href: `/${gameDomain}/dcatalog` },
      { id: 'guides', title: '攻略', href: `/${gameDomain}/dguides` },
      { id: 'tools', title: '工具', href: `/${gameDomain}/dtools` },
      { id: 'benefits', title: '福利', href: `/${gameDomain}/dbenefits` },
      { id: 'community', title: '社区', href: `/${gameDomain}/dcommunity` },
    ],
    [gameDomain],
  );
  const nav = useSiteNavigation(view, navigation, true);
  const [query, setQuery] = useState('');
  const [searchHits, setSearchHits] = useState<WikiSearchItem[]>([]);
  const [homepageConfig, setHomepageConfig] = useState<HomepageConfig>(() =>
    serverHomepageConfig || initialHomepageConfig(gameKey, gameDomain, gameName, gameIcon),
  );
  const [homepageConfigReady, setHomepageConfigReady] = useState(!!serverHomepageConfig);

  useEffect(() => {
    if (!clientBootstrap) return;
    let cancelled = false;
    const bootstrap = async () => {
      try {
        const [heroesRes, catalogsRes, homepage] = await Promise.all([
          fetch(`/api/games/${encodeURIComponent(gameKey)}/catalog-heroes`, {
            cache: 'no-store',
            credentials: 'same-origin',
          }),
          fetch(`/api/games/${encodeURIComponent(gameKey)}/catalogs`, {
            cache: 'no-store',
            credentials: 'same-origin',
          }),
          fetchHomepageConfig(gameKey).catch(() => null),
        ]);
        const heroesPayload = heroesRes.ok
          ? ((await heroesRes.json()) as { heroes?: HeroData[] })
          : { heroes: [] };
        const heroes = Array.isArray(heroesPayload.heroes) ? heroesPayload.heroes : [];
        const directory = heroes.map(heroToDirectoryEntry);
        const banners = catalogHeroesForBanner(heroes);
        const slots = bannerSlotsFromCatalog(
          banners,
          featuredHeroes.map((item) => item.id),
        );
        let nextNavigation = initialCatalogNavigation;
        if (catalogsRes.ok) {
          const catalogsPayload = (await catalogsRes.json()) as {
            catalogs?: Parameters<typeof parseCatalogNavigation>[1];
          };
          nextNavigation = parseCatalogNavigation(gameKey, catalogsPayload.catalogs || []);
        }
        const nextHomepage = homepage
          ? rewriteHomepageSitePaths(homepage, gameKey, gameDomain)
          : null;
        if (cancelled) return;
        setHeroDirectory(directory);
        setBannerHeroes(banners);
        setBannerLive2dSlots(slots);
        setCatalogNavigation(nextNavigation);
        if (nextHomepage) {
          setHomepageConfig(nextHomepage);
          setHomepageConfigReady(true);
        }
        setShellDataReady(true);
        void getCatalogUpdates(gameKey, gameDomain, nextNavigation, directory)
          .then((updates) => { if (!cancelled) setCatalogUpdates(updates); })
          .catch((error) => console.warn('Catalog updates load failed', error));
      } catch (error) {
        console.warn('Legacy shell client bootstrap failed', error);
        if (!cancelled) setShellDataReady(true);
      }
    };
    void bootstrap();
    return () => {
      cancelled = true;
    };
    // 仅首屏 bootstrap 一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientBootstrap, gameKey, gameDomain]);

  const [gameProfile, setGameProfile] = useState<WikiGameProfile>({
    key: gameKey,
    name: gameName,
    icon: gameIcon || '',
    domain: gameDomain,
  });
  const visibleNavigation = useMemo(
    () =>
      navigation.filter((item) => {
        // 配置未就绪前不展示可选 tab，避免「先显示再关掉」闪一下
        if (item.id === 'tools')
          return homepageConfigReady && homepageConfig.toolsEnabled;
        if (item.id === 'benefits')
          return homepageConfigReady && homepageConfig.benefitsEnabled;
        return true;
      }),
    [
      homepageConfig.benefitsEnabled,
      homepageConfig.toolsEnabled,
      homepageConfigReady,
      navigation,
    ],
  );
  const [wikiUser, setWikiUser] = useState<WikiUser | null>(null);
  const [permission, setPermission] = useState<WikiPermission>({
    role: '',
    canManageWiki: false,
    canEditProfile: false,
    canManageAdmins: false,
    canManagePermissions: false,
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [communityFilter, setCommunityFilter] = useState('社区');
  const requestedCommunityFilter = routeSearchParams.get('category');
  const activeCommunityFilter = communityFilter;
  const [guideSlideIndex, setGuideSlideIndex] = useState(0);
  const previousGuideSlide = useRef<number | null>(null);
  const [guideCarouselPaused, setGuideCarouselPaused] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const guidePosts = useRef<GuideSummary[]>([]);
  const guidePostsReady = useRef(false);
  const guidePostsFailed = useRef(false);
  const scripts = useRef<HTMLScriptElement[]>([]);

  useLayoutEffect(() => {
    if (!clientBootstrap || !shellDataReady || !root.current) return;
    applyLegacyShellDynamicContent(root.current, {
      gameDomain,
      heroDirectory,
      catalogNavigation,
    });
  }, [
    clientBootstrap,
    shellDataReady,
    gameDomain,
    heroDirectory,
    catalogNavigation,
  ]);

  useLayoutEffect(() => {
    if (!clientBootstrap || !shellDataReady || !root.current || !catalogUpdates) return;
    updateFeaturedCatalogCards(root.current, catalogUpdates);
  }, [clientBootstrap, shellDataReady, catalogUpdates]);

  useEffect(() => {
    const keyword = query.trim();
    if (!keyword) {
      setSearchHits([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void fetchWikiSearch(gameKey, keyword, controller.signal, SUGGESTION_LIMIT)
        .then((items) => {
          setSearchHits(items);
        })
        .catch((error) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setSearchHits([]);
        });
    }, SUGGESTION_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [gameKey, query]);
  useLayoutEffect(() => {
    Object.entries(selectors).forEach(([key, selector]) => {
      const node = root.current?.querySelector<HTMLElement>(selector);
      if (node) node.hidden = key !== nav.active;
    });
  }, [nav.active, contentMarkup]);
  useEffect(() => {
    const requestedDirectory = new URLSearchParams(window.location.search).get('category');
    if (
      requestedDirectory &&
      communityDirectories.includes(requestedDirectory as (typeof communityDirectories)[number])
    ) setCommunityFilter(requestedDirectory);
    else if (window.location.hash === '#community-announcements') setCommunityFilter('公告');
    const syncFilter = (event: Event) => {
      const button = (event.target as Element | null)?.closest<HTMLElement>('[data-filter]');
      const filter = button?.dataset.filter;
      if (!filter || !communityDirectories.includes(filter as (typeof communityDirectories)[number])) return;
      setCommunityFilter(filter);
      window.history.replaceState(
        null,
        '',
        `/${gameDomain}/dcommunity?category=${encodeURIComponent(filter)}`,
      );
    };
    const node = root.current;
    node?.addEventListener('click', syncFilter);
    return () => node?.removeEventListener('click', syncFilter);
  }, [gameDomain, requestedCommunityFilter]);
  useEffect(() => {
    if (nav.active !== 'community') return;
    root.current
      ?.querySelectorAll<HTMLElement>('[data-filter]')
      .forEach((button) => {
        const selected = button.dataset.filter === activeCommunityFilter;
        button.classList.toggle('active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    root.current
      ?.querySelectorAll<HTMLElement>('.player-post')
      .forEach((post) => {
        post.hidden = post.dataset.category !== activeCommunityFilter;
      });
  }, [activeCommunityFilter, nav.active]);
  useEffect(() => {
    if (nav.active !== 'community') return;
    const container = root.current;
    const list = container?.querySelector<HTMLElement>('.community-layout .post-list');
    if (!container || !list) return;
    let cancelled = false;
    type CommunityPost = {
      id: string;
      type?: string;
      category?: string | null;
      title: string;
      excerpt?: string;
      author?: string;
      publishedAt?: string;
    };
    const createLink = container.querySelector<HTMLAnchorElement>(
      '.community-create-post',
    );
    if (createLink) createLink.href = `/${gameDomain}/dcommunity/create`;
    const applyFilter = () => {
      const filter = container.dataset.communityActive || '社区';
      list.querySelectorAll<HTMLElement>('.player-post').forEach((post) => {
        post.hidden = post.dataset.category !== filter;
      });
    };
    const renderPosts = (posts: CommunityPost[]) => {
      if (cancelled) return;
      list.replaceChildren();
      const visible = posts.filter((post) => post.type !== 'guide' && post.id);
      if (!visible.length) {
        const empty = document.createElement('p');
        empty.className = 'community-posts-empty';
        empty.textContent = '暂无帖子';
        list.appendChild(empty);
        return;
      }
      visible.forEach((post) => {
        const directory = communityDirectoryForPost(post);
        const article = document.createElement('article');
        article.className = 'player-post';
        article.dataset.category = directory;
        const meta = document.createElement('div');
        meta.className = 'post-meta';
        const avatar = document.createElement('span');
        avatar.className = 'avatar';
        const authorName = (post.author || '玩家').trim() || '玩家';
        avatar.textContent = authorName.slice(0, 1);
        meta.appendChild(avatar);
        meta.appendChild(document.createTextNode(` ${authorName} `));
        const time = document.createElement('span');
        time.textContent = `· ${formatCommunityRelativeTime(post.publishedAt)}`;
        meta.appendChild(time);
        const tag = document.createElement('span');
        tag.className = 'post-tag';
        tag.textContent = directory;
        meta.appendChild(tag);
        const title = document.createElement('a');
        title.className = 'post-title';
        const detailHref = `/${gameDomain}/guide/${encodeURIComponent(post.id)}`;
        title.href = detailHref;
        title.textContent = post.title;
        const excerpt = document.createElement('a');
        excerpt.className = 'post-excerpt';
        excerpt.href = detailHref;
        const excerptText = (post.excerpt || '').trim();
        // The API caps generated excerpts at 180 Unicode characters.
        excerpt.textContent = excerptText
          ? Array.from(excerptText).length >= 180 && !/[.…]$/.test(excerptText)
            ? `${excerptText}…`
            : excerptText
          : '暂无摘要';
        const bottom = document.createElement('div');
        bottom.className = 'post-bottom';
        const categoryLabel = document.createElement('span');
        categoryLabel.textContent = post.category || directory;
        bottom.appendChild(categoryLabel);
        article.appendChild(meta);
        article.appendChild(title);
        article.appendChild(excerpt);
        article.appendChild(bottom);
        list.appendChild(article);
      });
      applyFilter();
    };
    list.innerHTML = '<p class="community-posts-empty">正在读取社区帖子…</p>';
    fetch(`/api/games/${encodeURIComponent(gameKey)}/posts?summary=1`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('社区帖子读取失败');
        return response.json();
      })
      .then((payload) => {
        const posts = ((payload as { posts?: CommunityPost[] }).posts || []).sort(
          (left, right) =>
            String(right.publishedAt || '').localeCompare(
              String(left.publishedAt || ''),
            ),
        );
        renderPosts(posts);
      })
      .catch(() => {
        if (!cancelled) {
          list.innerHTML =
            '<p class="community-posts-empty">社区帖子加载失败，请稍后重试</p>';
        }
      });
    return () => {
      cancelled = true;
    };
  }, [gameDomain, gameKey, nav.active]);
  useEffect(() => {
    const node = root.current;
    const openCommunityFilter = (event: Event) => {
      if (!(event instanceof MouseEvent)) return;
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      // Only real filter links — not the shell's data-community-active attribute.
      const link = (event.target as Element | null)?.closest<HTMLAnchorElement>(
        'a[data-community-filter]',
      );
      const filter = link?.dataset.communityFilter;
      if (!filter) return;
      event.preventDefault();
      setCommunityFilter(filter);
      window.history.replaceState(
        null,
        '',
        `/${gameDomain}/dcommunity?category=${encodeURIComponent(filter)}`,
      );
    };
    node?.addEventListener('click', openCommunityFilter);
    return () => node?.removeEventListener('click', openCommunityFilter);
  }, [gameDomain]);
  useEffect(() => {
    const replaceMissingCatalogImage = (event: Event) => {
      const image = event.target;
      if (!(image instanceof HTMLImageElement)) return;
      const fallback = image.dataset.fallback;
      if (
        fallback &&
        image.src !== new URL(fallback, window.location.href).href
      ) {
        image.removeAttribute('data-fallback');
        image.src = fallback;
      }
    };
    const node = root.current;
    node?.addEventListener('error', replaceMissingCatalogImage, true);
    return () =>
      node?.removeEventListener('error', replaceMissingCatalogImage, true);
  }, []);
  useEffect(() => {
    // Bootstrap replaces the catalog DOM; initialize its legacy controller only
    // after the final nodes and navigation configuration have been installed.
    if (!shellDataReady) return;
    if (nav.active === 'guides') {
      Object.assign(window, {
        __GAME_WIKI_GUIDE_CATEGORIES__: homepageConfig.guideCategories,
      });
      window.dispatchEvent(
        new CustomEvent('game-wiki-guide-config', {
          detail: { categories: homepageConfig.guideCategories },
        }),
      );
    }
    if (nav.active === 'catalog') {
      Object.assign(window, {
        __GAME_WIKI_CATALOGS__: catalogNavigation,
      });
    }
    document.title = `${gameName} · ${navigation.find((item) => item.id === nav.active)?.title} · GAME WIKI`;
    let src = '';
    if (
      nav.active === 'community' &&
      !document.querySelector('.community-dialog-backdrop')
    ) {
      src = staticUrl('/legacy-game-wiki/community.js?v=stacked-login-4');
    }
    if (nav.active === 'catalog') {
      src = staticUrl('/legacy-game-wiki/catalog-types.js?v=shared-card-interactions-33');
    }
    if (src && !document.querySelector(`script[src="${src}"]`)) {
      const prefix = getStaticAssetPrefix();
      Object.assign(window, {
        __WIKI_IMAGE_CDN_HOST__: catalogCdnHost,
        ...(prefix ? { __WIKI_ASSET_PREFIX__: prefix } : {}),
      });
      const script = document.createElement('script');
      script.src = src;
      document.body.appendChild(script);
      scripts.current.push(script);
    }
  }, [
    catalogNavigation,
    gameName,
    homepageConfig.guideCategories,
    nav.active,
    navigation,
    shellDataReady,
  ]);
  useEffect(() => {
    if (nav.active !== 'guides') return;
    const container = root.current;
    const featured = container?.querySelector<HTMLElement>('.guides-featured');
    const track = container?.querySelector<HTMLElement>('.guide-carousel-track');
    const picks = container?.querySelector<HTMLElement>('.guides-picks');
    const dots = container?.querySelector<HTMLElement>('.guide-carousel-dots');
    const previous = container?.querySelector<HTMLButtonElement>('.guide-carousel-prev');
    const next = container?.querySelector<HTMLButtonElement>('.guide-carousel-next');
    if (!featured || !track || !picks || !dots) return;
    const recommendations = homepageConfig.guideRecommendations.slice(0, 8);
    featured.hidden = recommendations.length === 0;
    if (!recommendations.length) return;

    const categoryFor = (url: string) => {
      try {
        return new URL(url, window.location.origin).searchParams.get('type') || '精选攻略';
      } catch {
        return '精选攻略';
      }
    };
    track.replaceChildren();
    dots.replaceChildren();
    picks.replaceChildren();
    const kicker = document.createElement('p');
    kicker.className = 'guides-kicker';
    kicker.textContent = "EDITOR'S PICKS / 编辑精选";
    picks.appendChild(kicker);

    const slides = recommendations.map((item, index) => {
      const slide = document.createElement('button');
      slide.type = 'button';
      slide.className = `guide-cover${index ? ` guide-cover-alt-${((index - 1) % 3) + 1}` : ''}`;
      slide.dataset.carouselSlide = String(index);
      slide.style.backgroundImage = `linear-gradient(90deg,rgba(24,52,45,.94),rgba(24,52,45,.7)),url("${handleImgToWebp(item.image).replaceAll('"', '%22')}")`;
      slide.style.backgroundSize = 'cover';
      slide.style.backgroundPosition = 'center';
      const top = document.createElement('span');
      top.className = 'guide-cover-top';
      top.append('精选攻略');
      const edition = document.createElement('span');
      edition.textContent = `精选阅读 · ${String(index + 1).padStart(2, '0')}`;
      top.appendChild(edition);
      const body = document.createElement('div');
      const pill = document.createElement('span');
      pill.className = 'guide-pill';
      pill.textContent = categoryFor(item.url);
      const heading = document.createElement('h2');
      heading.textContent = item.title;
      const description = document.createElement('p');
      description.textContent = item.description;
      const link = document.createElement('span');
      link.className = 'guide-cover-link';
      link.textContent = '阅读攻略';
      const arrow = document.createElement('span');
      arrow.textContent = '↗';
      link.appendChild(arrow);
      body.appendChild(pill);
      body.appendChild(heading);
      body.appendChild(description);
      body.appendChild(link);
      slide.appendChild(top);
      slide.appendChild(body);
      slide.addEventListener('click', () => openGuide(item.url));
      track.appendChild(slide);

      const dot = document.createElement('button');
      dot.type = 'button';
      dot.dataset.carouselDot = String(index);
      dot.setAttribute('aria-label', `查看第 ${index + 1} 篇精选攻略`);
      dots.appendChild(dot);

      const pick = document.createElement('button');
      pick.type = 'button';
      const number = document.createElement('span');
      number.className = 'pick-number';
      number.textContent = String(index + 1).padStart(2, '0');
      const copy = document.createElement('div');
      const category = document.createElement('small');
      category.textContent = categoryFor(item.url);
      const title = document.createElement('h3');
      title.textContent = item.title;
      copy.appendChild(category);
      copy.appendChild(title);
      const pickArrow = document.createElement('span');
      pickArrow.textContent = '↗';
      pick.appendChild(number);
      pick.appendChild(copy);
      pick.appendChild(pickArrow);
      picks.appendChild(pick);
      return { slide, dot, pick, item };
    });

    let active = 0;
    let timer = 0;
    const show = (index: number, restart = true) => {
      active = (index + slides.length) % slides.length;
      slides.forEach(({ slide, dot, pick }, itemIndex) => {
        const selected = itemIndex === active;
        slide.classList.toggle('is-active', selected);
        slide.tabIndex = selected ? 0 : -1;
        slide.setAttribute('aria-hidden', String(!selected));
        dot.setAttribute('aria-pressed', String(selected));
        pick.setAttribute('aria-pressed', String(selected));
      });
      if (restart) start();
    };
    const start = () => {
      window.clearInterval(timer);
      if (slides.length > 1 && !matchMedia('(prefers-reduced-motion: reduce)').matches)
        timer = window.setInterval(() => show(active + 1, false), 5500);
    };
    slides.forEach(({ dot, pick, item }, index) => {
      dot.addEventListener('click', () => show(index));
      pick.addEventListener('click', () => openGuide(item.url));
    });
    const showPrevious = () => show(active - 1);
    const showNext = () => show(active + 1);
    previous?.addEventListener('click', showPrevious);
    next?.addEventListener('click', showNext);
    show(0);
    return () => {
      window.clearInterval(timer);
      previous?.removeEventListener('click', showPrevious);
      next?.removeEventListener('click', showNext);
    };
  }, [homepageConfig.guideRecommendations, nav.active, openGuide]);
  useEffect(() => {
    if (nav.active !== 'guides') return;
    if (!root.current) return;
    let selectedAlbum: HomepageConfig['guideAlbums'][number] | null = null;
    const albumHost = root.current.querySelector<HTMLElement>('.guide-authors');
    const albumDialog = root.current.querySelector<HTMLDialogElement>('#guide-dialog');
    const albumDialogBody = albumDialog?.querySelector<HTMLElement>('.guide-dialog-body');
    let previousHtmlOverflow: string | null = null;
    let previousBodyOverflow: string | null = null;
    const lockPageScroll = () => {
      if (previousHtmlOverflow !== null) return;
      previousHtmlOverflow = document.documentElement.style.overflow;
      previousBodyOverflow = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    };
    const unlockPageScroll = () => {
      if (previousHtmlOverflow === null) return;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow ?? '';
      previousHtmlOverflow = null;
      previousBodyOverflow = null;
    };
    albumDialog?.addEventListener('close', unlockPageScroll);
    albumHost?.replaceChildren();
    albumHost?.classList.add('guide-albums-sidebar');
    const openAlbumDialog = (album: HomepageConfig['guideAlbums'][number]) => {
      if (!albumDialog || !albumDialogBody) return;
      selectedAlbum = album;
      albumDialog.classList.add('guide-album-preview-dialog');
      albumDialogBody.replaceChildren();
      const hero = document.createElement('header');
      hero.className = 'guide-album-preview-hero';
      if (album.cover) hero.style.backgroundImage = `url("${guideAlbumCoverDisplayURL(album.cover).replaceAll('"', '%22')}")`;
      const heroCopy = document.createElement('div');
      const eyebrow = document.createElement('small');
      eyebrow.textContent = 'GUIDE COLLECTION';
      const dialogTitle = document.createElement('h1');
      dialogTitle.id = 'guide-dialog-title';
      dialogTitle.textContent = album.title;
      const count = document.createElement('span');
      count.textContent = `${album.postIds.length} 篇攻略`;
      heroCopy.appendChild(eyebrow);
      heroCopy.appendChild(dialogTitle);
      heroCopy.appendChild(count);
      hero.appendChild(heroCopy);
      const content = document.createElement('section');
      content.className = 'guide-album-preview-content';
      const contentHeading = document.createElement('div');
      const contentTitle = document.createElement('strong');
      contentTitle.textContent = '专辑内容';
      const contentCount = document.createElement('small');
      contentCount.textContent = `${album.postIds.length} ARTICLES`;
      contentHeading.appendChild(contentTitle);
      contentHeading.appendChild(contentCount);
      const articleList = document.createElement('div');
      articleList.className = 'guide-album-preview-list';
      album.postIds.forEach((postId, index) => {
        const post = guidePosts.current.find((item) => item.id === postId);
        if (!post) return;
        const articleLink = document.createElement('a');
        const href = `/${gameDomain}/guide/${post.id}`;
        articleLink.href = href;
        const order = document.createElement('span');
        order.textContent = String(index + 1).padStart(2, '0');
        const articleCopy = document.createElement('div');
        const articleTitle = document.createElement('strong');
        articleTitle.textContent = post.title;
        const articleMeta = document.createElement('small');
        articleMeta.textContent = `${post.category || '攻略'} · ${formatUpdatedAgo(post.publishedAt)}`;
        articleCopy.appendChild(articleTitle);
        articleCopy.appendChild(articleMeta);
        const arrow = document.createElement('i');
        arrow.textContent = '↗';
        articleLink.appendChild(order);
        articleLink.appendChild(articleCopy);
        articleLink.appendChild(arrow);
        articleLink.addEventListener('click', (event) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          albumDialog.close();
          openGuide(href);
        });
        articleList.appendChild(articleLink);
      });
      if (!articleList.childElementCount) {
        const message = document.createElement('p');
        message.textContent = guidePostsFailed.current
          ? '攻略加载失败，请稍后重试'
          : guidePostsReady.current ? '专辑暂无已发布攻略' : '正在加载攻略…';
        articleList.appendChild(message);
      }
      content.appendChild(contentHeading);
      content.appendChild(articleList);
      albumDialogBody.appendChild(hero);
      albumDialogBody.appendChild(content);
      if (!albumDialog.open) albumDialog.showModal();
      lockPageScroll();
    };
    const albums = homepageConfig.guideAlbums.filter((album) => album.postIds.length);
    if (albumHost) {
      const heading = document.createElement('div');
      heading.className = 'guide-authors-heading guide-albums-sidebar-heading';
      const headingTitle = document.createElement('h2');
      headingTitle.textContent = '攻略专辑';
      const headingHint = document.createElement('span');
      headingHint.textContent = 'COLLECTIONS';
      heading.appendChild(headingTitle);
      heading.appendChild(headingHint);
      const albumGrid = document.createElement('div');
      albumGrid.className = 'guide-album-sidebar-list';
      albums.forEach((album) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-pressed', 'false');
        if (album.cover) {
          const image = document.createElement('img');
          image.src = guideAlbumCoverDisplayURL(album.cover);
          image.alt = '';
          button.appendChild(image);
        }
        const copy = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = album.title;
        const count = document.createElement('small');
        count.textContent = `${album.postIds.length} 篇攻略`;
        copy.appendChild(title);
        copy.appendChild(count);
        button.appendChild(copy);
        button.addEventListener('click', () => {
          openAlbumDialog(album);
        });
        albumGrid.appendChild(button);
      });
      albumHost.appendChild(heading);
      if (albums.length) {
        albumHost.appendChild(albumGrid);
      } else {
        const emptyAlbums = document.createElement('p');
        emptyAlbums.className = 'guide-album-sidebar-empty';
        emptyAlbums.textContent = '暂时还没有攻略专辑';
        albumHost.appendChild(emptyAlbums);
      }
    }
    const refreshOpenAlbum = () => {
      if (selectedAlbum && albumDialog?.open) openAlbumDialog(selectedAlbum);
    };
    window.addEventListener('wiki-guide-list-ready', refreshOpenAlbum);
    return () => {
      window.removeEventListener('wiki-guide-list-ready', refreshOpenAlbum);
      albumDialog?.removeEventListener('close', unlockPageScroll);
      if (albumDialog?.open) albumDialog.close();
      unlockPageScroll();
      albumDialog?.classList.remove('guide-album-preview-dialog');
      albumHost?.classList.remove('guide-albums-sidebar');
      albumHost?.replaceChildren();
    };
  }, [gameDomain, homepageConfig.guideAlbums, nav.active, openGuide]);
  useEffect(() => {
    if (nav.active !== 'guides') return;
    let cancelled = false;
    guidePosts.current = [];
    guidePostsReady.current = false;
    guidePostsFailed.current = false;
    fetch(`/api/games/${encodeURIComponent(gameKey)}/posts?type=guide&summary=1`, {
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`攻略列表请求失败：${response.status}`);
        return response.json();
      })
      .then((postPayload) => {
        if (cancelled || !root.current) return;
        const posts = (postPayload as { posts?: GuideSummary[] }).posts || [];
        guidePosts.current = posts;
        guidePostsReady.current = true;
        window.dispatchEvent(new Event('wiki-guide-list-ready'));
        const categories = homepageConfig.guideCategories
          .map((name) => name.trim())
          .filter((name, index, items) => name && items.indexOf(name) === index);
        const list = root.current.querySelector<HTMLElement>('.guides-list');
        const filters =
          root.current.querySelector<HTMLElement>('.guides-filters');
        const empty = root.current.querySelector<HTMLElement>('.guides-empty');
        const renderPosts = (category?: string, postIds?: string[]) => {
          if (!list) return;
          const selectedIds = postIds ? new Set(postIds) : null;
          const visiblePosts = posts.filter((post) =>
            selectedIds ? selectedIds.has(post.id) : category ? post.category === category : true,
          );
          list.replaceChildren();
          visiblePosts.forEach((post) => {
            const link = document.createElement('a');
            link.className = 'guide-entry';
            const href = `/${gameDomain}/guide/${post.id}`;
            link.href = href;
            link.addEventListener('click', (event) => {
              if (
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              )
                return;
              event.preventDefault();
              openGuide(href);
            });
            const title = document.createElement('h3');
            title.textContent = post.title;
            const meta = document.createElement('span');
            meta.className = 'guide-row-meta';
            meta.textContent = formatUpdatedAgo(post.publishedAt);
            link.appendChild(title);
            link.appendChild(meta);
            list.appendChild(link);
          });
          if (empty) empty.hidden = visiblePosts.length > 0;
        };
        if (filters) {
          filters.replaceChildren();
          (categories.includes('全部') ? categories : ['全部', ...categories]).forEach(
            (name, index) => {
              const button = document.createElement('button');
              button.type = 'button';
              button.textContent = name;
              button.setAttribute('aria-pressed', String(index === 0));
              button.addEventListener('click', () => {
                filters
                  .querySelectorAll('button')
                  .forEach((item) =>
                    item.setAttribute('aria-pressed', String(item === button)),
                  );
                root.current
                  ?.querySelector('.guide-authors')
                  ?.querySelectorAll('button')
                  .forEach((item) => item.setAttribute('aria-pressed', 'false'));
                renderPosts(index === 0 ? undefined : name);
              });
              filters.appendChild(button);
            },
          );
        }
        renderPosts();
      })
      .catch(() => {
        if (cancelled) return;
        guidePostsFailed.current = true;
        window.dispatchEvent(new Event('wiki-guide-list-ready'));
      });
    return () => {
      cancelled = true;
    };
  }, [gameDomain, gameKey, homepageConfig.guideAlbums, homepageConfig.guideCategories, nav.active, openGuide]);
  useEffect(() => {
    if (nav.active !== 'catalog') return;
    const container = root.current;
    if (!container) return;
    // Cloudflare's worker DOM declarations overlap the browser DOM types used by
    // this client component, so narrow query results after selection instead of
    // using querySelector's generic constraint.
    const search = container.querySelector(
      '#catalog-search',
    ) as unknown as HTMLInputElement | null;
    const grade = container.querySelector(
      '#catalog-grade',
    ) as unknown as HTMLSelectElement | null;
    const job = container.querySelector(
      '#catalog-job',
    ) as unknown as HTMLSelectElement | null;
    const role = container.querySelector(
      '#catalog-role',
    ) as unknown as HTMLSelectElement | null;
    const element = container.querySelector(
      '#catalog-element',
    ) as unknown as HTMLSelectElement | null;
    const reset = container.querySelector(
      '#catalog-reset',
    ) as unknown as HTMLButtonElement | null;
    const count = container.querySelector(
      '#catalog-count',
    ) as unknown as HTMLElement | null;
    const empty = container.querySelector(
      '#catalog-empty',
    ) as unknown as HTMLElement | null;
    if (!search || !grade || !job || !element || !reset || !count || !empty)
      return;

    const normalize = (value?: string) =>
      (value || '').trim().toLocaleLowerCase('zh-CN');
    const roleByJob = (value: string) =>
      ['剑士', '拳手'].includes(value)
        ? '坦克'
        : ['牧师', '精灵'].includes(value)
          ? '辅助'
          : '输出';
    const filterCatalog = () => {
      const cards = Array.from(
        container.querySelectorAll<HTMLElement>(
          '#catalog-heroes-panel .catalog-grid .catalog-card',
        ),
      );
      const query = normalize(search.value);
      const selectedGrade = normalize(grade.value);
      const selectedJob = normalize(job.value);
      const selectedRole = normalize(role?.value);
      const selectedElement = normalize(element.value);
      let visible = 0;
      cards.forEach((card) => {
        const cardJob = normalize(card.dataset.job);
        const matches =
          normalize(card.dataset.name).includes(query) &&
          (!selectedGrade || normalize(card.dataset.grade) === selectedGrade) &&
          (!selectedJob || cardJob === selectedJob) &&
          (!selectedRole ||
            normalize(roleByJob(card.dataset.job || '')) === selectedRole) &&
          (!selectedElement ||
            normalize(card.dataset.element) === selectedElement);
        card.hidden = !matches;
        if (matches) visible += 1;
      });
      count.textContent = `显示 ${visible} / ${cards.length} 位英雄`;
      empty.hidden = visible !== 0;
    };
    const resetCatalog = () => {
      search.value = '';
      grade.value = '';
      job.value = '';
      if (role) role.value = '';
      element.value = '';
      filterCatalog();
    };
    const selects = [grade, job, element, ...(role ? [role] : [])];
    search.addEventListener('input', filterCatalog);
    selects.forEach((select) =>
      select.addEventListener('change', filterCatalog),
    );
    reset.addEventListener('click', resetCatalog);
    filterCatalog();
    return () => {
      search.removeEventListener('input', filterCatalog);
      selects.forEach((select) =>
        select.removeEventListener('change', filterCatalog),
      );
      reset.removeEventListener('click', resetCatalog);
    };
  }, [nav.active]);
  useEffect(() => {
    let active = true;
    if (!clientBootstrap) {
      if (!serverHomepageConfig) setHomepageConfigReady(false);
      if (!serverHomepageConfig && gameKey === wikiGame.key) {
        setHomepageConfig(readHomepageConfig(gameKey));
        setHomepageConfigReady(true);
      }
      fetchHomepageConfig(gameKey)
        .then((config) => {
          if (active) {
            setHomepageConfig(
              rewriteHomepageSitePaths(config, gameKey, gameDomain),
            );
            if (gameKey === wikiGame.key) saveHomepageConfig(gameKey, config);
            setHomepageConfigReady(true);
          }
        })
        .catch(() => {
          if (active) setHomepageConfigReady(true);
        });
    }
    let lastPermissionUid: string | number | null = null;
    const syncUser = () => {
      const user = readWikiUser();
      setWikiUser(user);
      if (!user || !readWikiToken()) {
        lastPermissionUid = null;
        setPermission(EMPTY_PERMISSION);
        return;
      }
      // myInfo 成功也会派发 auth 事件；同一用户不必重复拉权限
      const uid = user.uid ?? user.name;
      if (lastPermissionUid === uid) return;
      lastPermissionUid = uid;
      fetchMyWikiPermission(gameKey)
        .then((value) => { if (active) setPermission(value); })
        .catch(() => {
          if (!active) return;
          lastPermissionUid = null;
          setPermission(EMPTY_PERMISSION);
        });
    };
    syncUser();
    window.addEventListener(WIKI_AUTH_EVENT, syncUser);
    window.addEventListener('storage', syncUser);
    return () => {
      active = false;
      window.removeEventListener(WIKI_AUTH_EVENT, syncUser);
      window.removeEventListener('storage', syncUser);
    };
  }, [clientBootstrap, gameDomain, gameKey, serverHomepageConfig]);
  useEffect(() => {
    const updateClock = () => {
      const node = root.current?.querySelector('#current-date');
      const days = root.current?.querySelector('#server-days');
      const now = new Date();
      if (node) {
        node.textContent = new Intl.DateTimeFormat('zh-CN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
        }).format(now);
      }
      const launch = homepageConfig.launchDate.match(
        /^(\d{4})-(\d{2})-(\d{2})$/,
      );
      if (days && launch) {
        const todayUtc = Date.UTC(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
        );
        const launchUtc = Date.UTC(
          Number(launch[1]),
          Number(launch[2]) - 1,
          Number(launch[3]),
        );
        days.textContent = String(
          Math.max(0, Math.floor((todayUtc - launchUtc) / 86400000) + 1),
        );
      }
    };
    updateClock();
    const timer = window.setInterval(updateClock, 60000);
    return () => window.clearInterval(timer);
  }, [homepageConfig.launchDate]);
  useEffect(
    () => () => scripts.current.forEach((script) => script.remove()),
    [],
  );
  useEffect(() => {
    const count = homepageConfig.guideLinks.length;
    if (guideSlideIndex >= count) setGuideSlideIndex(0);
  }, [guideSlideIndex, homepageConfig.guideLinks.length]);
  useEffect(() => {
    if (
      nav.active !== 'home' ||
      guideCarouselPaused ||
      homepageConfig.guideLinks.length < 2
    )
      return;
    const timer = window.setInterval(() => {
      setGuideSlideIndex(
        (index) => (index + 1) % homepageConfig.guideLinks.length,
      );
    }, 5000);
    return () => window.clearInterval(timer);
  }, [guideCarouselPaused, homepageConfig.guideLinks.length, nav.active]);
  useEffect(() => {
    const section =
      root.current?.querySelector<HTMLElement>('.version-section');
    if (!section) return;
    const pause = () => setGuideCarouselPaused(true);
    const resume = () => setGuideCarouselPaused(false);
    section.addEventListener('mouseenter', pause);
    section.addEventListener('mouseleave', resume);
    section.addEventListener('focusin', pause);
    section.addEventListener('focusout', resume);
    return () => {
      section.removeEventListener('mouseenter', pause);
      section.removeEventListener('mouseleave', resume);
      section.removeEventListener('focusin', pause);
      section.removeEventListener('focusout', resume);
    };
  }, [nav.active]);
  useLayoutEffect(() => {
    const container = root.current;
    if (!container) return;
    const versionSection =
      container.querySelector<HTMLElement>('.version-section');
    const versionHeading = versionSection?.querySelector<HTMLElement>(
      '.section-title > span:first-child',
    );
    if (versionHeading)
      versionHeading.textContent = homepageConfig.versionTitle;
    const featured = versionSection?.querySelector<HTMLAnchorElement>(
      '.version-grid > a:first-child',
    );
    const activeGuide =
      homepageConfig.guideLinks[guideSlideIndex] ||
      homepageConfig.guideLinks[0];
    if (featured) {
      featured.href = activeGuide?.url || `/${gameDomain}/dguides`;
      featured.style.setProperty(
        '--homepage-version-cover',
        `url("${handleImgToWebp(activeGuide?.image || defaultHomepageConfig.featuredGuide.image).replaceAll('"', '%22')}")`,
      );
      featured.dataset.slide = String(guideSlideIndex);
      if (previousGuideSlide.current !== null &&
          previousGuideSlide.current !== guideSlideIndex &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        featured.animate(
          [
            { opacity: 0.82, filter: 'saturate(.82)' },
            { opacity: 1, filter: 'saturate(1)' },
          ],
          { duration: 360, easing: 'ease-out' },
        );
      }
      previousGuideSlide.current = guideSlideIndex;
      const title = featured.querySelector('h2');
      if (title)
        title.textContent =
          activeGuide?.title || homepageConfig.featuredGuide.title;
      const description = featured.querySelector<HTMLElement>(
        '.version-guide-description',
      );
      if (description) description.textContent = activeGuide?.description || '';
    }
    const guideNav = versionSection?.querySelector<HTMLElement>(
      '.dungeon-guide-grid',
    );
    if (guideNav) {
      const guides = homepageConfig.guideLinks;
      guideNav.replaceChildren(
        ...guides.map((guide, index) => {
          const link = document.createElement('a');
          link.href = guide.url || `/${gameDomain}/dguides`;
          if (index === guideSlideIndex) {
            link.classList.add('is-active');
            link.setAttribute('aria-current', 'true');
          }
          const title = document.createElement('b');
          title.textContent = guide.title;
          link.appendChild(title);
          link.addEventListener('mouseenter', () => setGuideSlideIndex(index));
          link.addEventListener('focus', () => setGuideSlideIndex(index));
          return link;
        }),
      );
      guideNav.style.gridTemplateRows = `repeat(${Math.max(guides.length, 1)}, minmax(0, 1fr))`;
      const currentLink = guideNav.querySelector<HTMLElement>('[aria-current="true"]');
      if (currentLink && guideNav.clientHeight > 0) {
        const viewport = guideNav.getBoundingClientRect();
        const item = currentLink.getBoundingClientRect();
        const top = viewport.top + guideNav.clientTop;
        const bottom = top + guideNav.clientHeight;
        const delta = item.top < top
          ? item.top - top
          : item.bottom > bottom ? item.bottom - bottom : 0;
        if (delta) guideNav.scrollTo({
          top: guideNav.scrollTop + delta,
          behavior: 'instant',
        });
      }
    }

    const noticePanel = container.querySelector<HTMLElement>('.notice-panel');
    const noticeMore =
      noticePanel?.querySelector<HTMLAnchorElement>('.section-title a');
    if (noticeMore) {
      noticeMore.href = `/${gameDomain}/dcommunity?category=${encodeURIComponent('公告')}`;
      noticeMore.removeAttribute('data-community-filter');
    }

    const homeBenefitItems = homepageConfig.benefits.filter((benefit) => benefit.label.trim());
    const benefitList =
      container.querySelector<HTMLElement>('.benefit-code-list');
    benefitList?.replaceChildren(
      ...(homeBenefitItems
        .slice(0, 3)
        .map((benefit) => {
          const row = document.createElement('div');
          row.className = 'benefit-code-row';
          const time = document.createElement('time');
          time.dateTime = benefit.updatedAt;
          const date = new Date(benefit.updatedAt);
          time.textContent = Number.isNaN(date.getTime())
            ? benefit.date
            : `${date.getMonth() + 1}.${String(date.getDate()).padStart(2, '0')} 更新`;
          const label = document.createElement('span');
          label.textContent = benefit.label;
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.redeemCode = benefit.code;
          button.textContent = '复制';
          row.appendChild(time);
          row.appendChild(label);
          row.appendChild(button);
          return row;
        }) ?? []),
    );

    const homeBenefits =
      container.querySelector<HTMLElement>('.benefits-section');
    const showHomeBenefits =
      homepageConfig.benefitsEnabled &&
      homepageConfig.homeSidebarModule === 'benefits';
    if (homeBenefits)
      homeBenefits.hidden = !homepageConfigReady || !showHomeBenefits;
    const communityHot =
      container.querySelector<HTMLElement>('.community-hot-section');
    if (communityHot)
      communityHot.hidden = !homepageConfigReady || showHomeBenefits;
    const communityHotMore =
      communityHot?.querySelector<HTMLAnchorElement>('.section-title a');
    if (communityHotMore)
      communityHotMore.href = `/${gameDomain}/dcommunity?category=${encodeURIComponent('社区')}`;
    const benefitsMore =
      homeBenefits?.querySelector<HTMLAnchorElement>('[data-benefits-more]');
    if (benefitsMore) {
      benefitsMore.href = `/${gameDomain}/dbenefits`;
      benefitsMore.hidden = homeBenefitItems.length <= 3;
    }

    const benefitsPageList =
      container.querySelector<HTMLElement>('.benefits-page-list');
    benefitsPageList?.replaceChildren(
      ...homepageConfig.benefits
        .filter((benefit) => benefit.label.trim() && benefit.code.trim())
        .map((benefit) => {
          const card = document.createElement('article');
          card.className = 'benefits-code-card';
          const heading = document.createElement('header');
          const time = document.createElement('time');
          time.dateTime = benefit.updatedAt;
          const date = new Date(benefit.updatedAt);
          time.textContent = Number.isNaN(date.getTime())
            ? benefit.date
            : `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} 更新`;
          const title = document.createElement('strong');
          title.textContent = benefit.label;
          const expiry = document.createElement('small');
          const expiryDate = benefit.expiresAt
            ? new Date(benefit.expiresAt)
            : null;
          const expired = Boolean(
            expiryDate &&
              !Number.isNaN(expiryDate.getTime()) &&
              expiryDate.getTime() < Date.now(),
          );
          expiry.textContent = expiryDate && !Number.isNaN(expiryDate.getTime())
            ? `${expired ? '已于' : '有效期至'} ${expiryDate.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}`
            : '永不过期';
          heading.appendChild(time);
          heading.appendChild(title);
          heading.appendChild(expiry);
          const codeRow = document.createElement('div');
          const status = document.createElement('span');
          status.textContent = expired ? '已过期' : '可兑换';
          if (expired) card.classList.add('is-expired');
          const code = document.createElement('code');
          code.textContent = benefit.code;
          const copy = document.createElement('button');
          copy.type = 'button';
          copy.dataset.redeemCode = benefit.code;
          copy.setAttribute('aria-label', `复制礼包码 ${benefit.code}`);
          copy.textContent = '复制';
          codeRow.appendChild(status);
          codeRow.appendChild(code);
          codeRow.appendChild(copy);
          card.appendChild(heading);
          card.appendChild(codeRow);
          return card;
        }),
    );

    container
      .querySelectorAll<HTMLAnchorElement>('.home-promotion-card')
      .forEach((card, index) => {
        const promotion = homepageConfig.promotions[index];
        card.hidden = !promotion;
        if (!promotion) return;
        card.href = promotion.url || `/${gameDomain}`;
        const image = card.querySelector<HTMLImageElement>('img');
        const title = card.querySelector<HTMLElement>('strong');
        const description = card.querySelector<HTMLElement>('small');
        if (image) {
          const src = homepageDisplayImage(promotion.image);
          if (src) {
            image.hidden = false;
            image.src = src;
          } else {
            image.removeAttribute('src');
            image.hidden = true;
          }
        }
        if (title) title.textContent = promotion.title;
        if (description) description.textContent = promotion.description;
      });

    const resourceRow = container.querySelector<HTMLElement>('.home-resource-row');
    resourceRow?.replaceChildren();
    homepageConfig.navigationSections.forEach((navigationSection) => {
      if (!resourceRow || !navigationSection.title.trim()) return;
      const section = document.createElement('section');
      section.className = `home-navigation-section is-${navigationSection.layout}-navigation`;
      section.dataset.homeNavigation = navigationSection.id;
      section.setAttribute('aria-label', `${navigationSection.title}导航`);
      const heading = document.createElement('div');
      heading.className = 'section-title';
      const headingTitle = document.createElement('span');
      headingTitle.textContent = navigationSection.title;
      heading.appendChild(headingTitle);
      const moreTargets: Record<string, string> = {
        newcomer: `/${gameDomain}/dguides`,
        catalog: `/${gameDomain}/dcatalog`,
        tools: `/${gameDomain}/dtools`,
      };
      if (moreTargets[navigationSection.id]) {
        const more = document.createElement('a');
        more.className = 'home-navigation-all';
        more.href = moreTargets[navigationSection.id];
        more.textContent = '查看更多';
        heading.appendChild(more);
      }
      const links = document.createElement('nav');
      links.className = navigationSection.layout === 'feature'
        ? 'game-tool-card-grid'
        : 'home-navigation-grid';
      links.setAttribute('aria-label', `${navigationSection.title}列表`);
      navigationSection.items
        .filter((item) => item.title.trim())
        .forEach((item) => {
          const link = document.createElement('a');
          link.href = item.url || `/${gameDomain}`;
          const copy = document.createElement('span');
          copy.className = navigationSection.layout === 'feature'
            ? 'tool-card-copy'
            : 'home-navigation-copy';
          const title = document.createElement('strong');
          title.textContent = item.title;
          copy.appendChild(title);
          if (navigationSection.showDescriptions && item.description.trim()) {
            const description = document.createElement('small');
            description.textContent = item.description;
            copy.appendChild(description);
          }
          const arrow = document.createElement('i');
          arrow.setAttribute('aria-hidden', 'true');
          arrow.textContent = navigationSection.layout === 'feature' ? '进入' : '→';
          if (navigationSection.layout === 'compact') {
            const displaySrc = homepageDisplayImage(item.image);
            if (displaySrc) {
              const image = document.createElement('img');
              image.className = 'home-navigation-thumb';
              image.src = displaySrc;
              image.alt = '';
              image.loading = 'lazy';
              image.width = 44;
              image.height = 44;
              link.appendChild(image);
            } else {
              const placeholder = document.createElement('span');
              placeholder.className = 'home-navigation-thumb is-placeholder';
              link.appendChild(placeholder);
            }
            link.appendChild(copy);
            link.appendChild(arrow);
          } else {
            const displaySrc = homepageDisplayImage(item.image);
            link.classList.toggle('has-image', Boolean(displaySrc));
            if (displaySrc) {
              link.style.setProperty(
                '--game-tool-card-image',
                `url("${displaySrc.replaceAll('"', '%22')}")`,
              );
            }
            link.appendChild(copy);
            link.appendChild(arrow);
          }
          links.appendChild(link);
        });
      section.appendChild(heading);
      section.appendChild(links);
      resourceRow.appendChild(section);
    });
    const homeTools = container.querySelector<HTMLElement>(
      '.game-tools-section',
    );
    if (homeTools) homeTools.hidden = true;
    const toolCards = container.querySelectorAll<HTMLElement>(
      '.tools-grid > .tool-entry',
    );
    toolCards.forEach((card, index) => {
      const item = homepageConfig.toolCards[index];
      card.hidden = !item;
      if (!item) return;
      card.dataset.href = item.url.trim();
      card.classList.toggle('has-link', Boolean(card.dataset.href));
      card.classList.toggle('has-image', Boolean(item.image));
      if (item.image) {
        card.style.setProperty(
          '--tool-card-image',
          `url("${handleImgToWebp(item.image).replaceAll('"', '%22')}")`,
        );
      } else {
        card.style.removeProperty('--tool-card-image');
      }
      const title = card.querySelector<HTMLElement>('h2');
      const description = card.querySelector<HTMLElement>('p');
      const action = card.querySelector<HTMLElement>(
        '.tool-entry-bottom > span:first-child',
      );
      if (title) title.textContent = item.title;
      if (description) description.textContent = item.description;
      if (action) action.textContent = item.url.trim() ? '打开工具' : '功能准备中';
    });
    const toolCount = container.querySelector<HTMLElement>(
      '.tools-intro > span:last-child',
    );
    if (toolCount)
      toolCount.textContent = `${homepageConfig.toolCards.length} 个工具模块`;
  }, [base, guideSlideIndex, homepageConfig, homepageConfigReady, nav.active]);
  useEffect(() => {
    if (nav.active !== 'home' || !homepageConfigReady) return;
    const container = root.current;
    if (!container) return;
    const noticePanel = container.querySelector<HTMLElement>('.notice-panel');
    const hotList = container.querySelector<HTMLElement>('.community-hot-list');
    let cancelled = false;
    type SidebarPost = {
      id: string;
      type?: string;
      category?: string | null;
      title: string;
      author?: string;
      publishedAt?: string;
      recommended?: boolean;
    };
    const noticeTag = (post: SidebarPost) => {
      const category = String(post.category || '');
      if (category.includes('更新')) return '更新' as const;
      if (category.includes('情报') || category.includes('资讯')) return '情报' as const;
      return '公告' as const;
    };
    const renderNotices = (posts: SidebarPost[]) => {
      if (cancelled || !noticePanel) return;
      noticePanel.querySelectorAll('.notice-item').forEach((item) => item.remove());
      const notices = posts
        .filter((post) => communityDirectoryForPost(post) === '公告')
        .slice(0, 3);
      if (!notices.length) {
        const empty = document.createElement('p');
        empty.className = 'notice-item community-hot-empty';
        empty.textContent = '暂无游戏公告';
        noticePanel.appendChild(empty);
        return;
      }
      notices.forEach((post) => {
        const tag = noticeTag(post);
        const article = document.createElement('article');
        article.className = 'notice-item';
        const type = document.createElement('span');
        type.className = `notice-type ${tag === '更新' ? 'is-update' : 'is-info'}`;
        type.textContent = `[${tag}]`;
        const heading = document.createElement('h2');
        const link = document.createElement('a');
        link.href = `/${gameDomain}/guide/${encodeURIComponent(post.id)}`;
        link.textContent = post.title;
        heading.appendChild(link);
        article.appendChild(type);
        article.appendChild(heading);
        noticePanel.appendChild(article);
      });
    };
    const renderHotPosts = (posts: SidebarPost[]) => {
      if (cancelled || !hotList) return;
      hotList.replaceChildren();
      const visible = posts
        .filter((post) => communityDirectoryForPost(post) === '社区')
        .slice(0, 4);
      visible.forEach((post) => {
        const link = document.createElement('a');
        link.className = 'community-hot-item';
        link.href = `/${gameDomain}/guide/${encodeURIComponent(post.id)}`;
        const title = document.createElement('strong');
        title.textContent = post.title;
        link.appendChild(title);
        hotList.appendChild(link);
      });
      if (!visible.length) {
        const empty = document.createElement('p');
        empty.className = 'community-hot-empty';
        empty.textContent = '暂无社区热帖';
        hotList.appendChild(empty);
      }
    };
    if (noticePanel) {
      noticePanel.querySelectorAll('.notice-item').forEach((item) => item.remove());
      const loading = document.createElement('p');
      loading.className = 'notice-item community-hot-empty';
      loading.textContent = '正在读取游戏公告…';
      noticePanel.appendChild(loading);
    }
    if (hotList) hotList.innerHTML = '<p class="community-hot-empty">正在读取社区热帖…</p>';
    fetch(`/api/games/${encodeURIComponent(gameKey)}/posts?summary=1`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('帖子列表读取失败');
        return response.json();
      })
      .then((payload) => {
        const posts = ((payload as { posts?: SidebarPost[] }).posts || [])
          .filter((post) => post.type !== 'guide' && post.id)
          .sort(
            (left, right) =>
              Number(Boolean(right.recommended)) - Number(Boolean(left.recommended)) ||
              String(right.publishedAt || '').localeCompare(String(left.publishedAt || '')),
          );
        renderNotices(posts);
        renderHotPosts(posts);
      })
      .catch(() => {
        renderNotices([]);
        renderHotPosts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [gameDomain, gameKey, homepageConfigReady, nav.active]);
  useEffect(() => {
    const container = root.current;
    if (!container) return;
    const openToolCard = (event: Event) => {
      const card = (event.target as Element | null)?.closest<HTMLElement>(
        '.tools-grid > .tool-entry',
      );
      if (!card || !container.contains(card)) return;
      const href = card.dataset.href?.trim();
      if (!href) return;
      window.location.assign(href);
    };
    container.addEventListener('click', openToolCard);
    return () => container.removeEventListener('click', openToolCard);
  }, []);
  useEffect(() => {
    const container = root.current;
    if (!container) return;
    const copyCode = async (event: Event) => {
      const button = (event.target as Element).closest<HTMLButtonElement>(
        '[data-redeem-code]',
      );
      if (!button || !container.contains(button)) return;
      const code = button.dataset.redeemCode || '';
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
        button.textContent = '已复制';
        button.classList.add('is-copied');
        window.setTimeout(() => {
          button.textContent = '复制';
          button.classList.remove('is-copied');
        }, 1400);
      } catch {
        button.textContent = '复制失败';
      }
    };
    container.addEventListener('click', copyCode);
    return () => container.removeEventListener('click', copyCode);
  }, []);
  const searchSuggestions = useMemo<SiteSearchSuggestion[]>(
    () =>
      searchHits.map((item) => ({
        id: item.id,
        title: item.title,
        meta: item.meta,
      })),
    [searchHits],
  );
  const search = {
    query,
    onQuery: setQuery,
    onSearch: () => {
      const keyword = query.trim();
      if (keyword)
        window.location.assign(
          `/${gameDomain}/search?q=${encodeURIComponent(keyword)}`,
        );
    },
    placeholder: '搜索攻略，或问一个游戏问题',
    suggestions: searchSuggestions,
    onSuggestion: (suggestion: SiteSearchSuggestion) => {
      const hit = searchHits.find((item) => item.id === suggestion.id);
      if (hit?.href) window.location.assign(hit.href);
    },
  };
  const showWikiManagement = Boolean(
    wikiUser && readWikiToken() &&
    permission.canManageWiki,
  );
  const toggleEditor = () => {
    setEditorOpen((open) => !open);
  };
  const saveHomepage = async () => {
    if (!permission.canManageWiki) return window.alert('当前账号暂无 Wiki 管理权限，此处仅供预览。');
    const token = readWikiToken();
    if (!token) return window.alert('登录状态已失效，请重新登录。');
    try {
      if (permission.canEditProfile) {
        if (!gameProfile.name.trim()) throw new Error('请输入游戏名称');
        if (!gameProfile.icon) throw new Error('请上传游戏图标');
        if (!/^(?=.*[a-z])[a-z0-9-]{3,32}$/.test(gameProfile.domain)) {
          throw new Error('域名需为 3–32 位小写字母、数字或连字符，且不能全是数字');
        }
      }
      const savedProfile = permission.canEditProfile
        ? await updateWikiGameProfile(gameKey, gameProfile, token)
        : gameProfile;
      const saved = await updateHomepageConfig(gameKey, homepageConfig, token);
      setHomepageConfig(saved);
      setGameProfile(savedProfile);
      if (gameKey === wikiGame.key) saveHomepageConfig(gameKey, saved);
      setEditorOpen(false);
      if (savedProfile.domain !== gameDomain) {
        const oldPrefix = `/${gameDomain}`;
        const nextPath = window.location.pathname.startsWith(oldPrefix)
          ? `/${savedProfile.domain}${window.location.pathname.slice(oldPrefix.length)}`
          : `/${savedProfile.domain}`;
        window.location.assign(`${nextPath}${window.location.search}${window.location.hash}`);
      } else {
        router.refresh();
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '首页配置保存失败');
    }
  };
  return (
    <div
      className={`shared-game-shell shared-game-shell--${nav.active}`}
      data-game={gameKey}
      data-community-active={nav.active === 'community' ? activeCommunityFilter : undefined}
      ref={root}
    >
      <SiteHeader banner gameDomain={gameDomain} />
      <SiteHero
        title={`${gameName}wiki攻略站`}
        eyebrow={`${gameName.toUpperCase()} · COMMUNITY WIKI`}
        live2dSlots={bannerLive2dSlots}
        bannerHeroes={bannerHeroes}
        gameId={gameKey}
        initialTheme={gameKey === 'arcanatactics' ? 'light' : undefined}
        background={
          homepageConfig.heroBackground || defaultHomepageConfig.heroBackground
        }
        {...search}
      >
        <div className="gk-recommendations">
          {[
            { label: '新手怎么配队？', term: '队伍' },
            { label: '本期角色值得抽吗？', term: '抽卡' },
            { label: '装备词条怎么选？', term: '装备' },
          ].map((item) => (
            <button
              key={item.label}
              onClick={() =>
                window.location.assign(
                  `/${gameDomain}/search?q=${encodeURIComponent(item.term)}`,
                )
              }
            >
              {item.label}
            </button>
          ))}
        </div>
      </SiteHero>
      <SiteNavigation
        items={visibleNavigation}
        ready={homepageConfigReady || shellDataReady}
        active={nav.active}
        compact={nav.compact}
        marker={nav.marker}
        gameDomain={gameDomain}
        onNavigate={(event, id) => {
          if (nav.navigate(event, id)) setQuery('');
        }}
        sectionFilter={
          nav.active === 'community'
            ? {
                itemId: 'community',
                value: activeCommunityFilter,
                options: communityDirectories,
                onSelect: (value) => {
                  setCommunityFilter(value);
                  window.history.replaceState(
                    null,
                    '',
                    `/${gameDomain}/dcommunity?category=${encodeURIComponent(value)}`,
                  );
                },
              }
            : undefined
        }
        {...search}
      />
      <div className="shared-game-content" aria-busy={!shellDataReady}>
        {contentNode}
      </div>
      {showWikiManagement && (
        <HomeAdminEditor
          gameKey={gameKey}
          gameDomain={gameDomain}
          gameProfile={gameProfile}
          config={homepageConfig}
          open={editorOpen}
          defaultTab={nav.active}
          canEditProfile={permission.canEditProfile}
          canManageAdmins={permission.canManageAdmins}
          onChange={setHomepageConfig}
          onGameProfileChange={setGameProfile}
          onClose={toggleEditor}
          onSave={saveHomepage}
        />
      )}
    </div>
  );
}
